/**
 * The local, uncommitted part of a turn: draft placements, the selected rack
 * tile, the local rack order (persisted per room + game), blank letters (kept
 * on the placements), the keyboard cursor and transient UI flags.
 *
 * The rules live in the pure modules game/draft.ts and game/keyboard.ts; this
 * store just holds state and persists the rack order.
 */
import { create } from 'zustand';
import type { Board, Coord, Direction, GameView } from '@wordloom/engine';
import { CENTER } from '@wordloom/engine';
import {
  insertAtVisibleIndex,
  orderRack,
  placeTile,
  recallTile,
  reconcileDraft,
  shuffleOrder,
  swapInOrder,
  visibleRackIds,
  type DraftPlacement,
  type ReconcileResult,
} from '../game/draft';
import { KEYS, isStringArray, readJSON, removeKey, writeJSON } from '../lib/storage';

export interface BlankPickerState {
  tileId: string;
  row: number;
  col: number;
  /** 'place': a blank just dropped (Escape returns it); 'change': re-letter a placed blank. */
  mode: 'place' | 'change';
}

export interface DraftState {
  /** `${code}:${gameNumber}` of the game this draft belongs to. */
  key: string | null;
  rackOrder: string[];
  placements: DraftPlacement[];
  selectedId: string | null;
  cursor: Coord | null;
  direction: Direction;
  /** Newly drawn tiles to animate into the rack. */
  freshIds: string[];
  /** Cells to ring red after a rejected submit. */
  invalidCells: Coord[];
  /** Bumped to replay the shake animation. */
  shakeNonce: number;
  pending: boolean;
  picker: BlankPickerState | null;
  /** Tile currently being dragged (hidden at its origin). */
  draggingId: string | null;
  /** Tile just dropped by drag — it must not layout-animate from its old slot. */
  droppedId: string | null;
  /** While dragging over the rack: the insertion index preview. */
  rackHoverIndex: number | null;

  /** Align with a new server view. Returns what changed (for toasts/animations). */
  syncWithView(code: string, gameNumber: number, game: GameView): ReconcileResult | null;
  reset(): void;
  select(id: string | null): void;
  /** Place (or move) a draft tile; false when the square is taken. */
  place(board: Board, p: DraftPlacement, opts?: { viaDrag?: boolean }): boolean;
  recall(tileId: string, rackIndex?: number): void;
  recallAll(): void;
  /** Reorder within the rack; `viaDrag` suppresses the moved tile's own layout glide (the ghost already landed). */
  moveInRack(tileId: string, toVisibleIndex: number, opts?: { viaDrag?: boolean }): void;
  swapInRack(a: string, b: string): void;
  shuffle(): void;
  setCursor(c: Coord | null): void;
  setDirection(d: Direction): void;
  setPicker(p: BlankPickerState | null): void;
  setBlankLetter(tileId: string, letter: string): void;
  markInvalid(cells: Coord[]): void;
  clearInvalid(): void;
  setPending(p: boolean): void;
  setDragging(id: string | null): void;
  setRackHoverIndex(i: number | null): void;
  clearFresh(): void;
}

function persistOrder(key: string | null, order: string[]): void {
  if (!key) return;
  const [code, game] = key.split(':');
  if (code && game) writeJSON(KEYS.rack(code, Number(game)), order);
}

const initial = {
  key: null,
  rackOrder: [],
  placements: [],
  selectedId: null,
  cursor: null,
  direction: 'H' as Direction,
  freshIds: [],
  invalidCells: [],
  shakeNonce: 0,
  pending: false,
  picker: null,
  draggingId: null,
  droppedId: null,
  rackHoverIndex: null,
} satisfies Partial<DraftState>;

export const useDraftStore = create<DraftState>()((set, get) => ({
  ...initial,

  syncWithView(code, gameNumber, game) {
    const key = `${code}:${gameNumber}`;
    const state = get();
    if (state.key !== key) {
      // A different room or a new game (rematch): start clean, restore the
      // locally stored rack order for this game if we have one (page reload).
      const stored = readJSON(KEYS.rack(code, gameNumber), isStringArray) ?? [];
      if (gameNumber > 1) removeKey(KEYS.rack(code, gameNumber - 1));
      const { order } = orderRack(stored, game.rack);
      set({ ...initial, key, rackOrder: order, cursor: null });
      persistOrder(key, order);
      return { placements: [], rackOrder: order, returned: [], dropped: [], added: [] };
    }
    const result = reconcileDraft(state, game);
    const selectedStillHere = state.selectedId != null && result.rackOrder.includes(state.selectedId);
    const picker =
      state.picker && result.placements.some((p) => p.tileId === state.picker?.tileId) ? state.picker : null;
    set({
      placements: result.placements,
      rackOrder: result.rackOrder,
      selectedId: selectedStillHere ? state.selectedId : null,
      freshIds: result.added.length ? result.added : state.freshIds,
      picker,
      invalidCells: result.returned.length || result.dropped.length ? [] : state.invalidCells,
    });
    persistOrder(key, result.rackOrder);
    return result;
  },

  reset() {
    set({ ...initial });
  },

  select(id) {
    set({ selectedId: id });
  },

  place(board, p, opts) {
    const next = placeTile(board, get().placements, p);
    if (!next) return false;
    set({
      placements: next,
      selectedId: null,
      invalidCells: [],
      droppedId: opts?.viaDrag ? p.tileId : null,
    });
    return true;
  },

  recall(tileId, rackIndex) {
    const { placements, rackOrder, key, picker } = get();
    const nextPlacements = recallTile(placements, tileId);
    let order = rackOrder;
    if (rackIndex !== undefined) {
      order = insertAtVisibleIndex(rackOrder, nextPlacements, tileId, rackIndex);
      persistOrder(key, order);
    }
    set({
      placements: nextPlacements,
      rackOrder: order,
      invalidCells: [],
      // Dropped onto a rack slot by drag: appear there, don't glide from the board.
      droppedId: rackIndex !== undefined ? tileId : null,
      picker: picker?.tileId === tileId ? null : picker,
    });
  },

  recallAll() {
    set({ placements: [], invalidCells: [], selectedId: null, picker: null, droppedId: null });
  },

  moveInRack(tileId, toVisibleIndex, opts) {
    const { rackOrder, placements, key } = get();
    const order = insertAtVisibleIndex(rackOrder, placements, tileId, toVisibleIndex);
    set({ rackOrder: order, droppedId: opts?.viaDrag ? tileId : null });
    persistOrder(key, order);
  },

  swapInRack(a, b) {
    const order = swapInOrder(get().rackOrder, a, b);
    set({ rackOrder: order, selectedId: null });
    persistOrder(get().key, order);
  },

  shuffle() {
    const { rackOrder, placements, key } = get();
    const visible = new Set(visibleRackIds(rackOrder, placements));
    const order = shuffleOrder(rackOrder, visible);
    set({ rackOrder: order, selectedId: null });
    persistOrder(key, order);
  },

  setCursor(c) {
    set({ cursor: c });
  },

  setDirection(d) {
    set({ direction: d });
  },

  setPicker(p) {
    set({ picker: p });
  },

  setBlankLetter(tileId, letter) {
    set({
      placements: get().placements.map((p) => (p.tileId === tileId ? { ...p, letter } : p)),
    });
  },

  markInvalid(cells) {
    set((s) => ({ invalidCells: cells, shakeNonce: s.shakeNonce + 1 }));
  },

  clearInvalid() {
    set({ invalidCells: [] });
  },

  setPending(p) {
    set({ pending: p });
  },

  setDragging(id) {
    set({ draggingId: id, rackHoverIndex: null });
  },

  setRackHoverIndex(i) {
    if (get().rackHoverIndex !== i) set({ rackHoverIndex: i });
  },

  clearFresh() {
    set({ freshIds: [] });
  },
}));

/** Default keyboard cursor: the centre square. */
export const DEFAULT_CURSOR: Coord = { row: CENTER, col: CENTER };

